import _keyBy from 'lodash/keyBy';
import {
  SERVER_TIME_FORMAT,
  SMARTSPOT_REASON,
  DISMISSED_SMART_SPOT_BANNER_VENUE_IDS_LOCAL_STORAGE_KEY,
} from 'constants/logic';
import { makeEntityScaffold } from '@classpass/saga-tools';
import { format } from 'date-fns';
import URLS from 'constants/urls';
import { transformClientResponse, renameKeys, transformKeysToSnakeCase } from 'helpers/core';
import { setKeyForUser, getKeyForUser } from 'helpers/local-storage';

const KEY_MAP = {
  ssEnabled: 'isEnabled',
  date: 'startDateTime',
};
const INVERTED_KEY_MAP = Object.entries(KEY_MAP).reduce((acc, [k, v]) => ({ ...acc, [v]: k }), {});
const NO_CONTENT = 204;
const SMARTSPOT_HTTP_CONFIG = Object.freeze({
  ignoreForbiddenError: true,
});

const get = (httpClient, venueId, date) => {
  const venueEndpoint = URLS.smartSpot.venueSettings(venueId);
  const schedulesEndpoint = URLS.smartSpot.scheduleDataByDate(venueId, date);

  const venuePromise = httpClient
    .get(venueEndpoint, SMARTSPOT_HTTP_CONFIG)
    .then(response => {
      if (response && response.status === NO_CONTENT) {
        return {};
      }
      return transformClientResponse(response);
    })
    .then(data => renameKeys(data, KEY_MAP));

  const schedulesPromise = httpClient
    .get(schedulesEndpoint, SMARTSPOT_HTTP_CONFIG)
    .then(transformClientResponse)
    .then(({ schedules }) =>
      schedules.map(({ scheduleId, ssEnabled: { reason, value } }) => ({
        scheduleId,
        isEnabled: value,
        reason,
      }))
    )
    .then(data => _keyBy(data, 'scheduleId'));

  return Promise.all([venuePromise, schedulesPromise]).then(([venueData, scheduleData]) => ({
    ...venueData,
    scheduleData,
  }));
};

const updateVenue = (httpClient, data, { venueId }) => {
  const endpoint = URLS.smartSpot.venueSettings(venueId);
  const payload = transformKeysToSnakeCase(data);
  return httpClient.post(endpoint, payload, SMARTSPOT_HTTP_CONFIG);
};

const updateSchedule = (httpClient, { ssEnabled }, { venueId, scheduleId }) => {
  const endpoint = URLS.smartSpot.scheduleSettings(venueId, scheduleId);
  const payload = transformKeysToSnakeCase({ ssEnabled });
  return httpClient.post(endpoint, payload, SMARTSPOT_HTTP_CONFIG);
};

const updateRecurring = (httpClient, { ssEnabled }, { venueId, classId, date }) => {
  const endpoint = URLS.smartSpot.recurringSettings(venueId);
  const payload = transformKeysToSnakeCase({
    ssEnabled,
    classId,
    dayOfWeek: format(date, 'EEEE').toUpperCase(),
    startTime: format(date, SERVER_TIME_FORMAT),
  });
  return httpClient.post(endpoint, payload, SMARTSPOT_HTTP_CONFIG);
};

const updateDismissedBannerVenueIds = (userId, venueId) => {
  const dismissedSSBannerVenueIds = getKeyForUser(userId, DISMISSED_SMART_SPOT_BANNER_VENUE_IDS_LOCAL_STORAGE_KEY, []);
  const dismissedSSBannerVenueIdsSet = new Set(dismissedSSBannerVenueIds);
  dismissedSSBannerVenueIdsSet.add(venueId);
  setKeyForUser(
    userId,
    DISMISSED_SMART_SPOT_BANNER_VENUE_IDS_LOCAL_STORAGE_KEY,
    Array.from(dismissedSSBannerVenueIdsSet)
  );
};

const resetSmartSpotData = (httpClient, venueId) => {
  const endpoint = URLS.smartSpot.reset(venueId);
  return httpClient.delete(endpoint, SMARTSPOT_HTTP_CONFIG);
};

const handler = ({ httpClient }, action) => {
  const { data = {}, meta } = renameKeys(action, INVERTED_KEY_MAP);
  const { classId, date = new Date(), dismissed, reason, scheduleId, userId, venueId, reset } = meta;

  let promise = Promise.resolve();
  // "arity" to distinguish GET (venueId & date) from everything else
  if (reset) {
    promise = resetSmartSpotData(httpClient, venueId);
  } else if (Object.keys(meta).length > 2) {
    if (userId && dismissed) {
      updateDismissedBannerVenueIds(userId, venueId);
    } else if (classId && reason === SMARTSPOT_REASON.RECURRING_RULE) {
      promise = updateRecurring(httpClient, data, meta);
    } else if (scheduleId && reason === SMARTSPOT_REASON.SCHEDULE_SETTING) {
      promise = updateSchedule(httpClient, data, meta);
    } else {
      promise = updateVenue(httpClient, data, meta);
    }
  }

  return promise.then(() => get(httpClient, venueId, date));
};

export default makeEntityScaffold('SAGA.API.VENUE.SMARTSPOT_DATA', handler, {
  defaultKey: (client, { meta: { venueId } }) => venueId,
});
